// DESIGN_DEVIATION: no prototype exists for the Organization screen yet; layout
// follows the TeamView list→detail idiom and sd-* tokens. Confirm with the design
// studio before hardening visuals.
import { useEffect, useMemo, useState } from 'react';

import { useAuth, useApiClient } from '../../providers/auth-provider';
import { useTenants } from '../../hooks/use-tenants';
import type { TenantResponse } from '../../lib/api-client';

import { GitBindingsEditor } from './GitBindingsEditor';

const box: React.CSSProperties = {
  padding: '5px 10px',
  fontSize: 12,
  border: '1px solid var(--border-strong)',
  borderRadius: 6,
  background: 'var(--surface)',
  color: 'var(--text)',
  outline: 'none',
  fontFamily: 'inherit',
};

const primaryBtn: React.CSSProperties = {
  padding: '6px 12px',
  background: 'var(--accent)',
  color: '#fff',
  border: 0,
  borderRadius: 6,
  fontSize: 12,
  cursor: 'pointer',
  fontWeight: 500,
  fontFamily: 'inherit',
};

const ghostBtn: React.CSSProperties = {
  padding: '5px 10px',
  background: 'var(--surface)',
  color: 'var(--text)',
  border: '1px solid var(--border-strong)',
  borderRadius: 6,
  fontSize: 12,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

export function TenantsView() {
  const { user } = useAuth();
  const apiClient = useApiClient();
  const { tenants, status, error, createTenant, reparentTenant, deleteTenant } = useTenants();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [actingWithin, setActingWithin] = useState<TenantResponse | null>(null);
  const [actionError, setActionError] = useState<string | undefined>();

  const canManage = user?.capabilities?.full_access === true;

  // Reset any acting-within context when leaving the screen (avoids a surprising
  // global tenant switch persisting into other views).
  // DESIGN_DEVIATION: a shell-level tenant switcher + persistent banner is the
  // eventual home for this; scoped to this view for now.
  useEffect(() => {
    return () => apiClient.setTenantContext(null);
  }, [apiClient]);

  const sorted = useMemo(() => [...tenants].sort((a, b) => a.path.localeCompare(b.path)), [tenants]);
  const selected = sorted.find((t) => t.id === selectedId) ?? null;
  const minDepth = sorted.length ? Math.min(...sorted.map((t) => t.depth)) : 0;

  async function run(fn: () => Promise<unknown>) {
    setActionError(undefined);
    try {
      await fn();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Action failed');
    }
  }

  function actWithin(t: TenantResponse | null) {
    apiClient.setTenantContext(t ? t.id : null);
    setActingWithin(t);
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--bg)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '16px 20px 12px', flexWrap: 'wrap' }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>Organization</div>
        <div style={{ fontSize: 11, color: 'var(--text-soft)' }}>
          {tenants.length} {tenants.length === 1 ? 'tenant' : 'tenants'}
        </div>
      </div>

      {actingWithin && (
        <div
          style={{
            margin: '0 20px 10px',
            padding: '8px 12px',
            background: 'var(--accent-soft)',
            border: '1px solid var(--accent)',
            borderRadius: 8,
            fontSize: 12,
            color: 'var(--text)',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <span>
            Acting within <strong>{actingWithin.name}</strong>{' '}
            <code style={{ color: 'var(--text-soft)' }}>{actingWithin.path}</code> — other screens now
            scope to this tenant.
          </span>
          <button type="button" style={{ ...ghostBtn, marginLeft: 'auto' }} onClick={() => actWithin(null)}>
            Return to home
          </button>
        </div>
      )}

      {status === 'loading' && <div style={{ padding: 20, fontSize: 12, color: 'var(--text-mute)' }}>Loading…</div>}
      {status === 'error' && (
        <div style={{ padding: 20, fontSize: 12, color: 'var(--kind-failure)' }}>{error ?? 'Failed to load.'}</div>
      )}
      {actionError && (
        <div style={{ margin: '0 20px 10px', fontSize: 12, color: 'var(--kind-failure)' }}>{actionError}</div>
      )}

      <div style={{ flex: 1, minHeight: 0, display: 'flex', gap: 0, borderTop: '1px solid var(--border)' }}>
        {/* Tree list */}
        <div style={{ width: 320, flexShrink: 0, overflowY: 'auto', borderRight: '1px solid var(--border)', padding: 8 }}>
          {sorted.map((t) => {
            const isSel = t.id === selectedId;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setSelectedId(t.id)}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '7px 8px',
                  paddingLeft: 8 + (t.depth - minDepth) * 16,
                  background: isSel ? 'var(--bg-alt)' : 'transparent',
                  border: 0,
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                <span style={{ fontSize: 13, color: 'var(--text)', fontWeight: isSel ? 600 : 400 }}>
                  {(t.depth - minDepth) > 0 && <span style={{ color: 'var(--text-mute)' }}>› </span>}
                  {t.name}
                </span>
                <span style={{ fontSize: 10, color: 'var(--text-soft)', marginLeft: 6 }}>/{t.slug}</span>
              </button>
            );
          })}
          {status === 'success' && sorted.length === 0 && (
            <div style={{ padding: 16, fontSize: 12, color: 'var(--text-mute)' }}>No tenants in scope.</div>
          )}
        </div>

        {/* Detail */}
        <div style={{ flex: 1, minWidth: 0, overflowY: 'auto', padding: 20 }}>
          {!selected && (
            <div style={{ fontSize: 12, color: 'var(--text-mute)' }}>Select a tenant to view its details.</div>
          )}
          {selected && (
            <TenantDetail
              tenant={selected}
              canManage={canManage}
              allTenants={sorted}
              onActWithin={() => actWithin(selected)}
              onCreateChild={(name, slug) => run(() => createTenant({ name, slug, parent_id: selected.id }))}
              onReparent={(parentId) => run(() => reparentTenant(selected.id, parentId))}
              onDelete={() =>
                run(async () => {
                  await deleteTenant(selected.id);
                  setSelectedId(null);
                })
              }
            />
          )}
        </div>
      </div>
    </div>
  );
}

function TenantDetail(props: {
  tenant: TenantResponse;
  canManage: boolean;
  allTenants: TenantResponse[];
  onActWithin: () => void;
  onCreateChild: (name: string, slug: string) => void;
  onReparent: (parentId: string) => void;
  onDelete: () => void;
}) {
  const { tenant, canManage, allTenants } = props;
  const [childName, setChildName] = useState('');
  const [childSlug, setChildSlug] = useState('');
  const [reparentTo, setReparentTo] = useState('');

  // Valid reparent targets: any tenant that is NOT this tenant or one of its
  // descendants (server also rejects cycles).
  const reparentTargets = allTenants.filter(
    (t) => t.id !== tenant.id && !t.path.startsWith(`${tenant.path}/`) && t.path !== tenant.path,
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18, maxWidth: 720 }}>
      <div>
        <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>{tenant.name}</div>
        <div style={{ fontSize: 12, color: 'var(--text-soft)', marginTop: 2 }}>
          <code>{tenant.path}</code> · depth {tenant.depth}
          {tenant.git_branch && <> · default branch <code>{tenant.git_branch}</code></>}
        </div>
        <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" style={ghostBtn} onClick={props.onActWithin}>
            Act within this tenant
          </button>
        </div>
      </div>

      <GitBindingsEditor tenantId={tenant.id} canManage={canManage} />

      {canManage && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>Manage</div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <input placeholder="Child name" value={childName} onChange={(e) => setChildName(e.target.value)} style={box} />
            <input
              placeholder="child-slug"
              value={childSlug}
              onChange={(e) => setChildSlug(e.target.value.toLowerCase())}
              style={box}
            />
            <button
              type="button"
              style={primaryBtn}
              disabled={!childName || !childSlug}
              onClick={() => {
                props.onCreateChild(childName, childSlug);
                setChildName('');
                setChildSlug('');
              }}
            >
              + Create child
            </button>
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <select value={reparentTo} onChange={(e) => setReparentTo(e.target.value)} style={{ ...box, cursor: 'pointer' }}>
              <option value="">Move under…</option>
              {reparentTargets.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.path}
                </option>
              ))}
            </select>
            <button type="button" style={ghostBtn} disabled={!reparentTo} onClick={() => props.onReparent(reparentTo)}>
              Reparent
            </button>
            <button
              type="button"
              style={{ ...ghostBtn, color: 'var(--kind-failure)', marginLeft: 'auto' }}
              onClick={() => {
                if (confirm(`Delete tenant "${tenant.name}"? It must have no child tenants or users.`)) props.onDelete();
              }}
            >
              Delete tenant
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
